import { useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { groupsApi } from '../../../services/groups-api-client';
import { mutationErrorMessage } from '../../../lib/mutation-error';
import { groupToPayload } from '../../../lib/groupPayload';
import { browserTimeZone, resolveTripTimeZone } from '../../../lib/tripTime';
import {
  failureLine,
  partitionStale,
  planRezone,
  rezonePayload,
  runRezone,
  type RezoneMove,
  type RezoneOutcome,
  type RezonePlan,
} from '../../../lib/rezoneItinerary';
import { buildRezoneNotice, type ItineraryChangeNotice } from '../../../lib/changeNotice';
import TimeZonePicker from '../TimeZonePicker';
import ChangeNoticeDialog from '../changes/ChangeNoticeDialog';
import RezoneDialog from './RezoneDialog';
import type { GroupDetail } from '../../../types/groups';

const eventWord = (n: number) => (n === 1 ? 'event' : 'events');

/**
 * The trip's time zone.
 *
 * Nothing in Studio could set it, so every group read as "no time zone set —
 * using yours" and a Lisbon trip made from Chicago showed Chicago times in the
 * itinerary, the guest page and the change notices. The API has always accepted
 * and stored it (groups.time_zone); only the UI was missing.
 *
 * Saved through groupToPayload for the reason that function documents. Changing
 * it does not move any existing INSTANT: stored times keep their moment and
 * simply read differently in the new zone. But every itinerary item also stores
 * its OWN zone, which wins over the trip's on the guest site, so events saved
 * before the trip had a zone would keep showing the host's zone forever. When
 * any exist, saving offers — before saving — to move them to the new zone
 * keeping their local times, one checkbox per event; only events saved under the
 * fallback zone by mistake start ticked. See lib/rezoneItinerary.
 *
 * Each move is a full-row save and the backend has no version to check, so the
 * group is fetched fresh first and an event that differs from the on-screen copy
 * is not saved (partitionStale). Afterwards, moved events guests can see are
 * OFFERED to the host as a change notice (ChangeNoticeDialog; "Don't send" is
 * its default). Nothing here ever sends.
 *
 * The same offer is reachable without a zone change, from the notice beneath the
 * picker: a trip whose zone is already right but whose events are not (the
 * original QA case), and the retry path when some event saves failed.
 */
export default function TimeZoneCard({ group, onSaved }: { group: GroupDetail; onSaved: () => void }) {
  const { timeZone: resolved, isFallback } = resolveTripTimeZone(group.timeZone);
  // An unset zone starts EMPTY rather than as the host's own: one click on
  // "Save" must never file a Lisbon trip under the zone the host happens to be in.
  const saved = isFallback ? '' : resolved;
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState<(RezoneOutcome & { toZone: string }) | null>(null);
  const [offer, setOffer] = useState<{ plan: RezonePlan; savesZone: boolean } | null>(null);
  const [notice, setNotice] = useState<ItineraryChangeNotice | null>(null);
  // What undoes the change the notice is about: the zone, or (a Review with no zone save) only the moved events.
  const [undoHint, setUndoHint] = useState('change the time zone back');

  const dirty = Boolean(draft) && draft !== saved;
  // Events still on some other zone than the one the trip already has.
  const stranded = !isFallback && !dirty ? planRezone(group.itineraryItems, saved) : null;

  /** Trip zone first, then each event; refresh once at the end whatever landed. */
  const commit = async (zone: string, moves: RezoneMove[], savesZone: boolean) => {
    setError('');
    setOutcome(null);
    setBusy(true);
    try {
      // Before anything is saved, read the group fresh. The on-screen copy may be
      // stale — both the events (checked by partitionStale below) and the group
      // row itself: updateGroup REPLACES the record, so building it from the
      // on-screen group would silently undo a co-host's newer story or hero edit.
      let fresh: GroupDetail;
      try {
        fresh = await groupsApi.getGroup(group.id);
      } catch (e) {
        setError(
          `Could not re-check the trip before saving, so nothing was changed. ${mutationErrorMessage(e)}`.trim(),
        );
        return;
      }
      const freshItems: GroupDetail['itineraryItems'] = fresh.itineraryItems;
      if (savesZone) {
        try {
          await groupsApi.updateGroup(group.id, groupToPayload(fresh, { timeZone: zone }));
        } catch (e) {
          // Nothing moves under a zone that did not save.
          setError(mutationErrorMessage(e) || 'Could not save the time zone.');
          return;
        }
      }
      if (moves.length > 0) {
        // An event that no longer matches the server's copy is reported, not saved.
        const { safe, stale } = partitionStale(moves, freshItems);
        // The whole tile through itineraryToPayload.
        const result = await runRezone(
          safe,
          (move) => groupsApi.upsertItineraryItem(group.id, rezonePayload(move)),
          mutationErrorMessage,
        );
        setOutcome({ moved: result.moved, failed: [...stale, ...result.failed], toZone: zone });
        // Offered, never sent: ChangeNoticeDialog asks, and "Don't send" is its default.
        setUndoHint(savesZone ? 'change the time zone back' : 'move those events back');
        if (group.published) setNotice(buildRezoneNotice(result.moved, zone));
      }
      onSaved();
    } finally {
      setBusy(false);
      setOffer(null);
    }
  };

  const save = () => {
    const plan = planRezone(group.itineraryItems, draft);
    // Nothing to offer: save the zone directly, as before.
    if (plan.moves.length === 0) void commit(draft, [], true);
    else setOffer({ plan, savesZone: true });
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3, gridColumn: { md: '1 / -1' } }}>
      <Typography variant="h6" fontWeight={800}>Time zone</Typography>
      <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
        Where the trip happens. Itinerary times are entered and shown in this zone, on
        Studio, the guest page and every change notice.
      </Typography>
      {isFallback ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This trip has no time zone set, so Studio is using yours ({resolved}). Pick the trip&apos;s
          own zone and save it.
        </Alert>
      ) : null}
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      {outcome && outcome.moved.length > 0 ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          Moved {outcome.moved.length} {eventWord(outcome.moved.length)} to {outcome.toZone}, keeping their local times.
        </Alert>
      ) : null}
      {outcome && outcome.failed.length > 0 ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {outcome.failed.length} {eventWord(outcome.failed.length)} could not be moved and
          still {outcome.failed.length === 1 ? 'uses' : 'use'} the old zone:
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {outcome.failed.map(({ move, message, kind }) => (
              <li key={move.item.id}>{failureLine({ move, message, kind })}</li>
            ))}
          </Box>
        </Alert>
      ) : null}
      {stranded && stranded.moves.length > 0 ? (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          action={(
            <Button color="inherit" size="small" disabled={busy} onClick={() => setOffer({ plan: stranded, savesZone: false })}>
              Review
            </Button>
          )}
        >
          {stranded.moves.length} {eventWord(stranded.moves.length)} still {stranded.moves.length === 1 ? 'is' : 'are'} set to
          a different zone than the trip, so guests see those times in the old zone.
        </Alert>
      ) : null}
      <Box sx={{ mt: 2, maxWidth: 420 }}>
        <TimeZonePicker
          value={draft}
          onChange={setDraft}
          disabled={busy}
          helperText="Events saved in another zone can be moved to this one, keeping their local times — you choose which."
        />
      </Box>
      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Button variant="contained" disabled={busy || !dirty} onClick={save}>
          {busy ? 'Saving…' : 'Save time zone'}
        </Button>
        {draft !== saved ? (
          <Button variant="text" disabled={busy} onClick={() => setDraft(saved)}>Cancel</Button>
        ) : null}
      </Stack>
      {offer ? (
        <RezoneDialog
          plan={offer.plan}
          savesZone={offer.savesZone}
          // Ticked only when the trip had no zone before this save AND the event sits in the
          // host's own browser zone; see defaultSelectedIds.
          defaults={{ tripHadZone: !isFallback, hostZone: browserTimeZone() }}
          busy={busy}
          onCancel={() => setOffer(null)}
          onConfirm={(selected) => commit(offer.plan.toZone, selected, offer.savesZone)}
        />
      ) : null}
      <ChangeNoticeDialog
        open={Boolean(notice)}
        onClose={() => setNotice(null)}
        group={group}
        notice={notice}
        undoHint={undoHint}
      />
    </Paper>
  );
}
